export const FACTOR_IVA = 1.13
export const TASA_IVA = 0.13
export const TASA_UNO_POR_CIENTO = 0.01
export const TASA_RETENCION_RENTA = 0.1
export const DECIMALES_PRECIO_UNITARIO = 8
export const MONTO_MINIMO_UNO_POR_CIENTO = 100

export type TipoMontoLinea = 'gravado' | 'exento' | 'noSujeto' | 'noGravado'
export type TipoDescuento = 'Porcentaje' | 'Monto'

export interface LineaDte {
  precio: unknown
  cantidad: unknown
  conIva?: boolean
  tipoDescuento?: TipoDescuento | string
  descuento?: unknown
  tipoMonto?: TipoMontoLinea
}

export interface MontosDeLinea {
  precioUni: number
  descuento: number
  venta: number
  ventaExacta: number
  tipoMonto: TipoMontoLinea
}

export interface OpcionesTotales {
  aplicaPercepcion?: boolean
  aplicaRetencion?: boolean
  descuentoGlobal?: number
}

export interface TotalesDte {
  lineas: MontosDeLinea[]
  totalGravada: number
  totalExenta: number
  totalNoSuj: number
  totalNoGravado: number
  descuentoTotal: number
  sumaOperaciones: number
  iva: number
  ivaPercibido: number
  ivaRetenido: number
  subTotal: number
  montoTotalOperacion: number
  totalPagar: number
}

export const redondear2 = (valor: unknown): number => {
  const n = typeof valor === 'number' ? valor : parseFloat(`${valor ?? ''}`)
  if (!Number.isFinite(n)) return 0
  return Math.round(parseFloat((n * 100).toFixed(8))) / 100
}

export const aMonto = (valor: unknown): number => {
  const n = typeof valor === 'number' ? valor : parseFloat(`${valor ?? ''}`)
  return Number.isFinite(n) ? n : 0
}

export const precioUnitarioNeto = (precio: unknown, conIva?: boolean): number => {
  const valor = aMonto(precio)
  return conIva ? valor / FACTOR_IVA : valor
}

export const descuentoDeLinea = (linea: LineaDte): number => {
  const bruto = precioUnitarioNeto(linea.precio, linea.conIva) * aMonto(linea.cantidad)
  return linea.tipoDescuento === 'Porcentaje'
    ? redondear2((bruto * aMonto(linea.descuento)) / 100)
    : redondear2(linea.descuento)
}

export const ventaExactaDeLinea = (linea: LineaDte): number =>
  precioUnitarioNeto(linea.precio, linea.conIva) * aMonto(linea.cantidad) - descuentoDeLinea(linea)

export const montosDeLinea = (linea: LineaDte): MontosDeLinea => {
  const precioUni = precioUnitarioNeto(linea.precio, linea.conIva)
  const descuento = descuentoDeLinea(linea)
  const ventaExacta = precioUni * aMonto(linea.cantidad) - descuento
  return {
    precioUni,
    descuento,
    venta: redondear2(ventaExacta),
    ventaExacta,
    tipoMonto: linea.tipoMonto ?? 'gravado',
  }
}

export const totalConIvaDeLinea = (linea: LineaDte): number => {
  const montos = montosDeLinea(linea)
  return redondear2(
    montos.tipoMonto === 'gravado' ? montos.ventaExacta * FACTOR_IVA : montos.ventaExacta,
  )
}

export const ivaSobreBase = (baseGravada: number): number => redondear2(baseGravada * TASA_IVA)

export const unoPorCiento = (base: number): number => redondear2(base * TASA_UNO_POR_CIENTO)

export const retencionRenta = (base: number): number => redondear2(base * TASA_RETENCION_RENTA)

export const alcanzaElMinimoDelUnoPorCiento = (sumaOperaciones: number): boolean =>
  redondear2(sumaOperaciones) >= MONTO_MINIMO_UNO_POR_CIENTO

export const totalesDelDocumento = (
  lineas: LineaDte[],
  opciones: OpcionesTotales = {}
): TotalesDte => {
  const calculadas = lineas.map(montosDeLinea)

  const porTipo = (tipo: TipoMontoLinea) =>
    redondear2(
      calculadas.filter((l) => l.tipoMonto === tipo).reduce((acc, l) => acc + l.ventaExacta, 0)
    )

  const totalGravadaBruta = porTipo('gravado')
  const totalExenta = porTipo('exento')
  const totalNoSuj = porTipo('noSujeto')
  const totalNoGravado = porTipo('noGravado')
  const descuentoTotal = redondear2(calculadas.reduce((acc, l) => acc + l.descuento, 0))

  const descuentoGlobal = redondear2(opciones.descuentoGlobal ?? 0)
  const totalGravada = redondear2(totalGravadaBruta - descuentoGlobal)

  const baseGravadaExacta = calculadas
    .filter((l) => l.tipoMonto === 'gravado')
    .reduce((acc, l) => acc + l.ventaExacta, 0)

  const sumaOperaciones = redondear2(totalGravada + totalExenta + totalNoSuj)
  const iva = ivaSobreBase(baseGravadaExacta - descuentoGlobal)

  const alcanza = alcanzaElMinimoDelUnoPorCiento(sumaOperaciones)
  const ivaPercibido =
    opciones.aplicaPercepcion && alcanza ? redondear2(unoPorCiento(totalGravada) + unoPorCiento(totalNoSuj)) : 0
  const ivaRetenido = opciones.aplicaRetencion && alcanza ? unoPorCiento(totalGravada) : 0

  const subTotal = redondear2(sumaOperaciones + iva)
  const montoTotalOperacion = subTotal
  const totalPagar = redondear2(montoTotalOperacion + totalNoGravado + ivaPercibido - ivaRetenido)

  return {
    lineas: calculadas,
    totalGravada,
    totalExenta,
    totalNoSuj,
    totalNoGravado,
    descuentoTotal,
    sumaOperaciones,
    iva,
    ivaPercibido,
    ivaRetenido,
    subTotal,
    montoTotalOperacion,
    totalPagar,
  }
}

export const repartirPorBase = (total: number, bases: Array<number>): Array<number> => {
  const reparto = bases.map(() => 0)

  if (!(total > 0) || bases.length === 0) return reparto

  let basesUsadas = bases.map((base) => (isNaN(base) || base <= 0 ? 0 : base))
  let suma = basesUsadas.reduce((acumulado, base) => acumulado + base, 0)

  if (suma <= 0) {
    basesUsadas = bases.map(() => 1)
    suma = basesUsadas.length
  }

  let indiceAjuste = -1
  basesUsadas.forEach((base, indice) => {
    if (base > 0 && (indiceAjuste < 0 || base > basesUsadas[indiceAjuste])) indiceAjuste = indice
  })

  if (indiceAjuste < 0) return reparto

  let restante = redondear2(total)

  basesUsadas.forEach((base, indice) => {
    if (indice === indiceAjuste || base <= 0) return
    const parte = redondear2((total * base) / suma)
    reparto[indice] = parte > restante ? restante : parte
    restante = redondear2(restante - reparto[indice])
  })

  reparto[indiceAjuste] = restante

  return reparto
}
